<app-modal
  [isOpen]="isOpen"
  (close)="onClose()"
  className="max-w-3xl mx-4 sm:mx-6"
>
  @if (producto) {
    <div class="p-6 sm:p-8">
      <!-- Header -->
      <div class="flex items-start justify-between gap-6 mb-8">
        <div class="flex items-center gap-3">
          @if (producto.imageUrl) {
            <img
              [src]="producto.imageUrl"
              [alt]="producto.name"
              class="w-14 h-14 rounded-lg object-cover"
            />
          }
          <div>
            <div class="flex items-center gap-2 mb-1">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                {{ producto.name }}
              </h3>
              <app-badge
                [color]="getTipoColor()"
                size="sm"
                >{{ getTipoLabel() }}</app-badge
              >
              <!-- Inc 53: subtipo de segundo nivel -->
              <app-badge
                color="light"
                size="sm"
                >{{ getSubtipoLabel() }}</app-badge
              >
              <app-badge
                [color]="getActiveColor()"
                size="sm"
                >{{ getActiveLabel() }}</app-badge
              >
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 font-mono">
              {{ producto.sku }}
            </p>
            @if (producto.description) {
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {{ producto.description }}
              </p>
            }
            @if (promoStatus) {
              <div class="flex gap-1 items-center">
                <span class="text-xs text-gray-500 dark:text-gray-400 font-mono"
                  >Promoción</span
                >
                <app-badge
                  [color]="promoStatus.color"
                  size="sm"
                  >{{ promoStatus.text }}</app-badge
                >
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
        @for (tab of tabs; track tab.id) {
          <button
            type="button"
            (click)="onTabChange(tab.id)"
            class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
            [class.border-brand-500]="activeTab === tab.id"
            [class.text-brand-600]="activeTab === tab.id"
            [class.dark:text-brand-400]="activeTab === tab.id"
            [class.border-transparent]="activeTab !== tab.id"
            [class.text-gray-500]="activeTab !== tab.id"
            [class.hover:text-gray-700]="activeTab !== tab.id"
            [class.dark:text-gray-400]="activeTab !== tab.id"
          >
            {{ tab.label }}
          </button>
        }
      </div>

      <!-- Content -->
      <!-- ===== INFORMACIÓN ===== -->
      @if (activeTab === 'info') {
        <div class="space-y-5">
          <section>
            <h4
              class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
            >
              Precios
            </h4>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p class="text-xs text-gray-500">Precio</p>
                <div class="font-semibold text-brand-600 dark:text-brand-400">
                  @if (promoActiva) {
                    <p>
                      {{
                        formatPrecio(
                          producto.promoPrice,
                          producto.prefix,
                          producto.currencyCode
                        )
                      }}
                    </p>
                  }
                  <p
                    [class.line-through]="promoActiva"
                    [class.text-xs]="promoActiva"
                  >
                    {{
                      formatPrecio(
                        producto.price,
                        producto.prefix,
                        producto.currencyCode
                      )
                    }}
                  </p>
                </div>
              </div>
              <div>
                <p class="text-xs text-gray-500">Costo</p>
                <p class="text-gray-800 dark:text-gray-200">
                  {{ formatPrecio(producto.cost) }}
                </p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Margen</p>
                <p class="text-gray-800 dark:text-gray-200">
                  {{
                    producto.margin !== undefined && producto.margin !== null
                      ? producto.margin + '%'
                      : '-'
                  }}
                </p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Precio mín.</p>
                <p class="text-gray-800 dark:text-gray-200">
                  {{ formatPrecio(producto.minPrice) }}
                </p>
              </div>
            </div>
          </section>
          <section class="pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4
              class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
            >
              Impuestos y descuentos
            </h4>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p class="text-xs text-gray-500">Impuesto %</p>
                <p class="text-gray-800 dark:text-gray-200">
                  {{ producto.taxPercent ?? '-'
                  }}{{
                    producto.taxPercent !== undefined &&
                    producto.taxPercent !== null
                      ? '%'
                      : ''
                  }}
                </p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Gravable</p>
                <p class="text-gray-800 dark:text-gray-200">
                  {{ producto.isTaxable ? 'Sí' : 'No' }}
                </p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Dto. máx.</p>
                <p class="text-gray-800 dark:text-gray-200">
                  {{ producto.maxDiscountPercent ?? '-'
                  }}{{
                    producto.maxDiscountPercent !== undefined &&
                    producto.maxDiscountPercent !== null
                      ? '%'
                      : ''
                  }}
                </p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Tier pricing</p>
                <p class="text-gray-800 dark:text-gray-200">
                  {{ producto.usesTierPricing ? 'Sí' : 'No' }}
                </p>
              </div>
            </div>
          </section>

          @if (producto.type !== 'service') {
            <section class="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4
                class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
              >
                Inventario
              </h4>
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <p class="text-xs text-gray-500">Unidad</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ producto.unitOfMeasure || '-' }}
                  </p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Rastreable</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ producto.isTrackable ? 'Sí' : 'No' }}
                  </p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Stock</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ producto.stockQuantity ?? '-' }}
                  </p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Stock mín.</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ producto.minStockLevel ?? '-' }}
                  </p>
                </div>
              </div>
            </section>
          } @else {
            <section class="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4
                class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
              >
                Servicio
              </h4>
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <p class="text-xs text-gray-500">Horas</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ producto.durationHours ?? '-' }}
                  </p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Recurrente</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ producto.isRecurring ? 'Sí' : 'No' }}
                  </p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Ciclo</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ producto.billingCycle || '-' }}
                  </p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Condición</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ producto.billingCondition || '-' }}
                  </p>
                </div>
              </div>
            </section>
          }

          <!-- ===== Vigencia promocional (Incidente #57) ===== -->
          <section class="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div class="flex items-center gap-2 mb-3">
              <h4
                class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide"
              >
                Vigencia promocional
              </h4>
              @if (tienePromocion) {
                <app-badge
                  [color]="getPromoStatusColor()"
                  size="sm"
                  >{{ getPromoStatusLabel() }}</app-badge
                >
              }
            </div>
            @if (!tienePromocion) {
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Este producto no tiene una promoción configurada.
              </p>
            } @else {
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <p class="text-xs text-gray-500">Precio promocional</p>
                  <p class="font-semibold text-brand-600 dark:text-brand-400">
                    {{
                      formatPrecio(
                        producto.promoPrice,
                        producto.prefix,
                        producto.currencyCode
                      )
                    }}
                  </p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Desde</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ formatDateOnly(producto.promoValidFrom) }}
                  </p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Hasta</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ formatDateOnly(producto.promoValidUntil) }}
                  </p>
                </div>
                <div>
                  <!-- Es el interruptor `promoEnabled`, no el estado: rotularlo
                       "Promoción activa" hacía que la ficha dijera "Inactiva" y
                       "Sí" a la vez. -->
                  <p class="text-xs text-gray-500">Interruptor</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ producto.promoEnabled === false ? 'No' : 'Sí' }}
                  </p>
                </div>
              </div>
            }
          </section>

          <section class="pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4
              class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
            >
              Historial de precios (últimos 5)
            </h4>
            @if (loadingHistory) {
              <div class="flex justify-center py-4">
                <div
                  class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"
                ></div>
              </div>
            } @else if (priceHistory.length === 0) {
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Sin cambios registrados.
              </p>
            } @else {
              <div class="space-y-1 text-sm">
                @for (h of priceHistory; track h.id) {
                  <div
                    class="flex items-center justify-between gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-800/40 rounded"
                  >
                    <span class="text-gray-700 dark:text-gray-300"
                      >{{ formatPrecio(h.oldPrice) }} →
                      <span
                        class="font-semibold text-brand-600 dark:text-brand-400"
                        >{{ formatPrecio(h.newPrice) }}</span
                      ></span
                    >
                    <span class="text-xs text-gray-500">{{
                      formatDate(h.datetimeChanged)
                    }}</span>
                  </div>
                }
              </div>
            }
          </section>

          <section class="pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4
              class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
            >
              Metadata
            </h4>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p class="text-xs text-gray-500">Categoría</p>
                <p class="text-gray-800 dark:text-gray-200">
                  {{ getCategoryName() }}
                </p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Creado</p>
                <p class="text-gray-800 dark:text-gray-200">
                  {{ formatDate(producto.datetimeCreated) }}
                </p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Actualizado</p>
                <p class="text-gray-800 dark:text-gray-200">
                  {{ formatDate(producto.datetimeUpdated) }}
                </p>
              </div>
            </div>
          </section>
        </div>
      }

      <!-- ===== ADJUNTOS ===== -->
      @if (activeTab === 'attachments') {
        @if (loadingAttachments) {
          <div class="flex justify-center py-10">
            <div
              class="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"
            ></div>
          </div>
        } @else if (attachmentsError) {
          <!-- Inc 58: antes cualquier fallo se mostraba como "Sin adjuntos". -->
          <div class="text-center py-10">
            <p class="text-sm text-error-600 dark:text-error-400">
              {{ attachmentsError }}
            </p>
          </div>
        } @else if (attachments.length === 0) {
          <div
            class="text-center py-10 text-sm text-gray-500 dark:text-gray-400"
          >
            Sin adjuntos.
          </div>
        } @else {
          <div class="space-y-1.5">
            @for (a of attachments; track a.id) {
              <div
                class="flex items-center gap-2 text-sm px-2 py-1.5 bg-gray-50 dark:bg-gray-800/40 rounded"
              >
                @if (a.thumbnailUrl || (!esArchivoSubido(a) && a.fileUrl)) {
                  <img
                    [src]="a.thumbnailUrl || a.fileUrl"
                    class="w-8 h-8 rounded object-cover shrink-0"
                  />
                }
                <div class="flex-1 min-w-0">
                  <button
                    type="button"
                    (click)="descargarAdjunto(a)"
                    class="text-brand-600 dark:text-brand-400 hover:underline truncate block text-left w-full"
                  >
                    {{ a.filename || a.fileUrl }}
                  </button>
                  @if (a.description) {
                    <p class="text-xs text-gray-500 truncate">
                      {{ a.description }}
                    </p>
                  }
                </div>
                <span
                  class="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                  >{{ attachmentTypeLabel(a.attachmentType) }}</span
                >
              </div>
            }
          </div>
        }
      }

      <!-- ===== PRECIOS ESCALONADOS ===== -->
      @if (activeTab === 'tierPrices') {
        @if (loadingTierPrices) {
          <div class="flex justify-center py-10">
            <div
              class="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"
            ></div>
          </div>
        } @else if (tierPrices.length === 0) {
          <div
            class="text-center py-10 text-sm text-gray-500 dark:text-gray-400"
          >
            Sin precios escalonados.
          </div>
        } @else {
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50 dark:bg-gray-800/40">
              <tr>
                <th class="px-2 py-1.5 text-left text-xs text-gray-500">
                  Nivel
                </th>
                <th class="px-2 py-1.5 text-left text-xs text-gray-500">
                  Método
                </th>
                <th class="px-2 py-1.5 text-right text-xs text-gray-500">
                  Rango
                </th>
                <th class="px-2 py-1.5 text-right text-xs text-gray-500">
                  P. unitario
                </th>
                <th class="px-2 py-1.5 text-right text-xs text-gray-500">
                  P. fijo
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-white/5">
              @for (t of tierPrices; track t.id) {
                <tr>
                  <td class="px-2 py-1.5 text-gray-700 dark:text-gray-300">
                    {{ t.tierLevel }}
                  </td>
                  <td class="px-2 py-1.5 text-xs text-gray-500">
                    {{ pricingMethodLabel(t.pricingMethod) }}
                  </td>
                  <td
                    class="px-2 py-1.5 text-right text-gray-700 dark:text-gray-300"
                  >
                    {{ t.minUnits }} - {{ t.maxUnits ?? '∞' }}
                  </td>
                  <td
                    class="px-2 py-1.5 text-right text-gray-700 dark:text-gray-300"
                  >
                    {{ formatPrecio(t.unitPrice) }}
                  </td>
                  <td
                    class="px-2 py-1.5 text-right text-gray-700 dark:text-gray-300"
                  >
                    {{ formatPrecio(t.fixedPrice) }}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      }

      <!-- ===== PRECIOS POR MONEDA ===== -->
      @if (activeTab === 'priceCurrencies') {
        @if (loadingPriceCurrencies) {
          <div class="flex justify-center py-10">
            <div
              class="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"
            ></div>
          </div>
        } @else if (priceCurrencies.length === 0) {
          <div
            class="text-center py-10 text-sm text-gray-500 dark:text-gray-400"
          >
            Sin precios por moneda.
          </div>
        } @else {
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50 dark:bg-gray-800/40">
              <tr>
                <th class="px-2 py-1.5 text-left text-xs text-gray-500">
                  Moneda
                </th>
                <th class="px-2 py-1.5 text-left text-xs text-gray-500">
                  Tipo
                </th>
                <th class="px-2 py-1.5 text-right text-xs text-gray-500">
                  P. fijo
                </th>
                <th class="px-2 py-1.5 text-right text-xs text-gray-500">
                  P. resuelto
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-white/5">
              @for (pc of priceCurrencies; track pc.id) {
                <tr>
                  <td class="px-2 py-1.5 text-gray-700 dark:text-gray-300">
                    <span [title]="'Identificador interno de la moneda: ' + pc.currencyId">{{ pc.currencyCode || '—' }}</span>
                  </td>
                  <td class="px-2 py-1.5 text-xs text-gray-500">
                    {{ priceCurrencyTypeLabel(pc.priceType) }}
                  </td>
                  <td
                    class="px-2 py-1.5 text-right text-gray-700 dark:text-gray-300"
                  >
                    {{ formatPrecio(pc.fixedPrice, pc.currencySymbol) }}
                  </td>
                  <td
                    class="px-2 py-1.5 text-right font-semibold text-brand-600 dark:text-brand-400"
                  >
                    @if (
                      pc.priceType === 'calculated' &&
                      (pc.usdValueUsed === null ||
                        pc.usdValueUsed === undefined)
                    ) {
                      <span
                        class="text-warning-600 dark:text-warning-400"
                        title="La moneda no tiene tipo de cambio configurado"
                        >Sin tasa</span
                      >
                    } @else {
                      {{ formatPrecio(pc.resolvedPrice, pc.currencySymbol) }}
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      }

      <!-- ===== ATRIBUTOS DE VARIANTES ===== -->
      @if (activeTab === 'variantAttributes') {
        @if (loadingVariantAttributes) {
          <div class="flex justify-center py-10">
            <div
              class="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"
            ></div>
          </div>
        } @else if (variantAttributes.length === 0) {
          <div
            class="text-center py-10 text-sm text-gray-500 dark:text-gray-400"
          >
            Sin atributos de variantes.
          </div>
        } @else {
          <div class="space-y-2">
            @for (attr of variantAttributes; track attr.id) {
              <div class="flex items-start gap-2">
                <span
                  class="text-sm font-medium text-gray-800 dark:text-gray-200 min-w-20"
                  >{{ attr.name }}:</span
                >
                <div class="flex flex-wrap gap-1">
                  @for (v of attr.values; track v.id) {
                    <span
                      class="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                      >{{ v.value }}</span
                    >
                  }
                </div>
              </div>
            }
          </div>
        }
      }

      <!-- ===== VARIANTES ===== -->
      @if (activeTab === 'variants') {
        @if (loadingVariants) {
          <div class="flex justify-center py-10">
            <div
              class="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"
            ></div>
          </div>
        } @else if (variants.length === 0) {
          <div
            class="text-center py-10 text-sm text-gray-500 dark:text-gray-400"
          >
            Sin variantes.
          </div>
        } @else {
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50 dark:bg-gray-800/40">
              <tr>
                <th class="px-2 py-1.5 text-left text-xs text-gray-500">SKU</th>
                <th class="px-2 py-1.5 text-left text-xs text-gray-500">
                  Atributos
                </th>
                <th class="px-2 py-1.5 text-right text-xs text-gray-500">
                  Stock
                </th>
                <th class="px-2 py-1.5 text-right text-xs text-gray-500">
                  P. efectivo
                </th>
                <th class="px-2 py-1.5 text-center text-xs text-gray-500">
                  Activo
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-white/5">
              @for (v of variants; track v.id) {
                <tr>
                  <td
                    class="px-2 py-1.5 font-mono text-xs text-gray-700 dark:text-gray-300"
                  >
                    {{ v.sku }}
                  </td>
                  <td class="px-2 py-1.5 text-xs text-gray-500">
                    {{ v.attributesSummary || '-' }}
                  </td>
                  <td
                    class="px-2 py-1.5 text-right text-gray-700 dark:text-gray-300"
                  >
                    {{ v.stockQuantity ?? '-' }}
                  </td>
                  <td
                    class="px-2 py-1.5 text-right font-semibold text-brand-600 dark:text-brand-400"
                  >
                    {{ formatPrecio(v.effectivePrice) }}
                  </td>
                  <td class="px-2 py-1.5 text-center text-xs">
                    {{ v.isActive ? '✓' : '✗' }}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      }

      <!-- ========== TAB: TIMELINE ========== -->
      @if (activeTab === 'timeline') {
        <app-audit-timeline
          [logs]="timelineLogs"
          [loading]="loadingTimeline"
          [error]="timelineError"
          [currentPage]="timelinePage"
          [totalPages]="timelineTotalPages"
          [totalElements]="timelineTotalElements"
          emptyMessage="No hay eventos de auditoría para este negocio."
          (pageChange)="loadTimeline($event)"
        />
      }
      <!-- end timeline tab -->

      <!-- ===== NOTAS INTERNAS (Incidente #26) ===== -->
      @if (activeTab === 'internalNotes' && producto) {
        <app-producto-internal-notes
          [productId]="producto.id"
        ></app-producto-internal-notes>
      }
    </div>
  }
</app-modal>
